import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import LanguageToggle from "../components/LanguageToggle";
import SofiaChatWidget from "../components/SofiaChatWidget";
import {
  Calendar, MessageCircle, Bot, Receipt, CreditCard,
  Package, BarChart3, Users, Check, ArrowRight, Sparkles,
  Zap, Clock, Globe, Shield, ChevronRight,
} from "lucide-react";
import { FaPaypal, FaBitcoin } from "react-icons/fa";
import { SiTether } from "react-icons/si";

/**
 * LandingPage — public marketing site at commandixcrm.com (root).
 *
 * Visual: pure black (#000) + orange (#FF7A00) accents, Outfit + Work Sans.
 * Sections: Nav, Hero (with parallax logo), Social Proof, Problem, Features Bento,
 * How It Works, Pricing, Business Types, Testimonials, FAQ, Final CTA, Footer.
 */
export default function LandingPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [scrolled, setScrolled] = useState(false);

  // Parallax watermark — same effect as the Verizon CRM Layout
  const handleMouseMove = (e) => {
    setMousePos({
      x: (e.clientX / window.innerWidth - 0.5) * 30,
      y: (e.clientY / window.innerHeight - 0.5) * 30,
    });
  };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div
      className="min-h-screen bg-black text-white overflow-x-hidden relative"
      style={{ fontFamily: "'Work Sans', sans-serif" }}
      onMouseMove={handleMouseMove}
      data-testid="landing-page"
    >
      {/* Parallax watermark — solo el isotipo con cables (sin texto) flotando
          como sombra detrás de toda la pagina. */}
      <div
        className="fixed pointer-events-none select-none"
        style={{
          width: "min(45vh, 45vw)",
          height: "min(45vh, 45vw)",
          top: "50%",
          left: "50%",
          opacity: 0.07,
          transform: `translate(-50%, -50%) translate(${mousePos.x}px, ${mousePos.y}px)`,
          transition: "transform 0.7s cubic-bezier(0.25,0.46,0.45,0.94)",
          zIndex: 0,
        }}
      >
        <img
          src="/brand/commandix-isotipo-cables.png"
          alt=""
          className="w-full h-full object-contain"
          draggable="false"
        />
      </div>

      {/* Subtle radial gradient glow */}
      <div
        className="fixed inset-0 pointer-events-none z-0"
        style={{
          background:
            "radial-gradient(60% 50% at 50% 0%, rgba(255,122,0,0.08), transparent 80%)",
        }}
      />

      {/* Sticky nav */}
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled
            ? "bg-black/80 backdrop-blur-xl border-b border-zinc-900"
            : "bg-transparent"
        }`}
        style={{ paddingTop: "env(safe-area-inset-top)" }}
        data-testid="landing-nav"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center" data-testid="nav-logo">
            <img
              src="/brand/commandix-logo-tight.png"
              alt="Commandix"
              className="h-10 md:h-11 object-contain"
              draggable="false"
            />
          </Link>
          <div className="hidden md:flex items-center gap-8 text-sm text-zinc-400">
            <a href="#features" className="hover:text-white transition" data-testid="nav-features">{t("nav.features")}</a>
            <a href="#pricing" className="hover:text-white transition" data-testid="nav-pricing">{t("nav.pricing")}</a>
            <a href="#how" className="hover:text-white transition" data-testid="nav-how">{t("nav.how")}</a>
          </div>
          <div className="flex items-center gap-2">
            <LanguageToggle className="hidden sm:inline-flex" />
            <Link to="/login" data-testid="nav-login">
              <Button variant="ghost" size="sm" className="text-zinc-300 hover:text-white hover:bg-white/5">
                {t("nav.login")}
              </Button>
            </Link>
            <Button
              onClick={() => navigate("/signup")}
              size="sm"
              className="bg-[#FF7A00] hover:bg-[#FF8E1F] text-black font-bold shadow-[0_0_20px_rgba(255,122,0,0.3)]"
              data-testid="nav-cta"
            >
              {t("nav.cta")}
            </Button>
          </div>
        </div>
      </nav>

      {/* HERO */}
      <section className="relative pt-44 pb-24 md:pt-52 md:pb-32 px-6 md:px-12 z-10">
        <div className="max-w-7xl mx-auto text-center">
          <Badge
            className="bg-[#FF7A00]/10 text-[#FF7A00] border border-[#FF7A00]/20 hover:bg-[#FF7A00]/20 mb-6 px-3 py-1 text-xs uppercase tracking-[0.2em] font-bold"
            data-testid="hero-badge"
          >
            <Sparkles size={12} className="mr-1.5" /> {t("hero.badge")}
          </Badge>
          <h1
            className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-extrabold tracking-tight leading-[0.95] mb-8"
            style={{ fontFamily: "'Outfit', sans-serif" }}
            data-testid="hero-title"
          >
            {t("hero.title_1")}
            <br />
            {t("hero.title_2")}{" "}
            <span className="text-[#FF7A00] relative inline-block">
              {t("hero.title_highlight")}
              <span className="absolute -bottom-2 left-0 right-0 h-1 bg-[#FF7A00] rounded-full opacity-50" />
            </span>
          </h1>
          <p
            className="text-lg md:text-xl text-zinc-400 max-w-2xl mx-auto mb-10 leading-relaxed"
            data-testid="hero-subtitle"
          >
            {t("hero.subtitle_1")} <span className="text-white font-semibold">{t("hero.subtitle_highlight")}</span>.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-4">
            <Button
              onClick={() => navigate("/signup")}
              size="lg"
              className="bg-[#FF7A00] hover:bg-[#FF8E1F] text-black font-bold text-base px-8 h-14 shadow-[0_0_40px_rgba(255,122,0,0.4)] hover:shadow-[0_0_60px_rgba(255,122,0,0.6)] hover:scale-105 transition-all"
              data-testid="hero-cta-primary"
            >
              {t("hero.cta_primary")} <ArrowRight className="ml-2" size={18} />
            </Button>
            <a href="#how">
              <Button
                size="lg"
                variant="outline"
                className="border-zinc-700 text-white hover:bg-white/5 hover:border-zinc-500 h-14 px-8"
                data-testid="hero-cta-secondary"
              >
                {t("hero.cta_secondary")}
              </Button>
            </a>
          </div>
          <p className="mt-6 text-xs text-zinc-500 uppercase tracking-[0.2em]">
            {t("hero.no_card")}
          </p>
        </div>
      </section>

      {/* SOCIAL PROOF */}
      <section className="relative py-12 px-6 md:px-12 border-y border-zinc-900 bg-zinc-950/50 backdrop-blur-sm z-10">
        <div className="max-w-7xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
          {[
            { num: "24/7", label: t("stats.auto") },
            { num: "+30", label: t("stats.types") },
            { num: "5min", label: t("stats.setup") },
            { num: "$0", label: t("stats.free") },
          ].map((s, i) => (
            <div key={i} data-testid={`stat-${i}`}>
              <p
                className="text-4xl md:text-5xl font-extrabold text-[#FF7A00]"
                style={{ fontFamily: "'Outfit', sans-serif" }}
              >
                {s.num}
              </p>
              <p className="text-xs uppercase tracking-[0.15em] text-zinc-500 mt-2">
                {s.label}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* NOT A GENERIC CRM */}
      <section className="relative py-24 md:py-32 px-6 md:px-12 z-10" data-testid="not-crm-section">
        <div className="max-w-5xl mx-auto text-center">
          <p className="text-xs uppercase tracking-[0.25em] text-[#FF7A00] font-bold mb-4">{t("not_crm.kicker")}</p>
          <h2 className="text-4xl md:text-6xl font-extrabold tracking-tight leading-tight mb-8" style={{ fontFamily: "'Outfit', sans-serif" }}>
            {t("not_crm.title_1")}{" "}
            <span className="text-zinc-600 line-through">{t("not_crm.title_strike")}</span>
            <br />
            <span className="text-[#FF7A00]">{t("not_crm.title_orange")}</span>.
          </h2>
          <p className="text-lg text-zinc-400 max-w-3xl mx-auto leading-relaxed">{t("not_crm.body")}</p>
        </div>
      </section>

      {/* BUSINESS TYPES — granular cards (6) */}
      <section className="relative py-24 px-6 md:px-12 border-y border-zinc-900 bg-zinc-950/40 z-10" data-testid="biztypes-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14">
            <p className="text-xs uppercase tracking-[0.25em] text-[#FF7A00] font-bold mb-4">{t("biztypes.kicker")}</p>
            <h2 className="text-4xl md:text-5xl font-extrabold tracking-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>{t("biztypes.title")}</h2>
            <p className="text-zinc-400 mt-3">{t("biztypes.subtitle")}</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              { icon: Sparkles, title: t("biztypes.card_beauty"), desc: t("biztypes.card_beauty_desc") },
              { icon: Package, title: t("biztypes.card_colmado"), desc: t("biztypes.card_colmado_desc") },
              { icon: Users, title: t("biztypes.card_callcenter"), desc: t("biztypes.card_callcenter_desc") },
              { icon: Zap, title: t("biztypes.card_services"), desc: t("biztypes.card_services_desc") },
              { icon: MessageCircle, title: t("biztypes.card_wasales"), desc: t("biztypes.card_wasales_desc") },
              { icon: Receipt, title: t("biztypes.card_resto"), desc: t("biztypes.card_resto_desc") },
            ].map((c, i) => (
              <div key={i} className="p-6 rounded-2xl bg-zinc-950 border border-zinc-900 hover:border-[#FF7A00]/40 hover:-translate-y-1 transition-all duration-300" data-testid={`biztype-card-${i}`}>
                <c.icon className="text-[#FF7A00] mb-4" size={26} />
                <h3 className="font-bold text-lg mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>{c.title}</h3>
                <p className="text-sm text-zinc-400 leading-relaxed">{c.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SMART MODULES */}
      <section className="relative py-24 px-6 md:px-12 z-10" data-testid="modules-section">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-14">
            <p className="text-xs uppercase tracking-[0.25em] text-[#FF7A00] font-bold mb-4">{t("modules.kicker")}</p>
            <h2 className="text-3xl md:text-5xl font-extrabold tracking-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>{t("modules.title")}</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {(t("modules.items", { returnObjects: true }) || []).map((m, i) => (
              <div key={i} className="p-4 rounded-xl bg-zinc-950 border border-zinc-900 hover:border-[#FF7A00]/30 transition flex items-start gap-3" data-testid={`module-item-${i}`}>
                <ChevronRight size={16} className="text-[#FF7A00] mt-0.5 flex-shrink-0" />
                <span className="text-sm text-zinc-300">{m}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SOFIA PROPOSAL */}
      <section className="relative py-24 md:py-32 px-6 md:px-12 z-10 border-y border-zinc-900 bg-gradient-to-b from-zinc-950/60 to-black" data-testid="sofia-proposal-section">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-[#FF7A00]/15 mb-6">
            <Bot className="text-[#FF7A00]" size={32} />
          </div>
          <p className="text-xs uppercase tracking-[0.25em] text-[#FF7A00] font-bold mb-4">{t("sofia_proposal.kicker")}</p>
          <h2 className="text-4xl md:text-5xl font-extrabold tracking-tight mb-6" style={{ fontFamily: "'Outfit', sans-serif" }}>{t("sofia_proposal.title")}</h2>
          <p className="text-lg text-zinc-400 max-w-2xl mx-auto leading-relaxed mb-10">{t("sofia_proposal.subtitle")}</p>
          <Button
            onClick={() => window.dispatchEvent(new CustomEvent("open-sofia-chat"))}
            size="lg"
            className="bg-[#FF7A00] hover:bg-[#FF8E1F] text-black font-bold text-base px-8 h-14 shadow-[0_0_40px_rgba(255,122,0,0.4)] hover:scale-105 transition-all"
            data-testid="sofia-proposal-cta"
          >
            {t("sofia_proposal.cta")} <ArrowRight className="ml-2" size={18} />
          </Button>
        </div>
      </section>

      {/* PROBLEM AGITATION */}
      <section className="relative py-24 md:py-32 px-6 md:px-12 z-10">
        <div className="max-w-5xl mx-auto">
          <p className="text-xs uppercase tracking-[0.25em] text-[#FF7A00] font-bold mb-4">
            {t("problem.kicker")}
          </p>
          <h2
            className="text-4xl md:text-6xl font-extrabold tracking-tight leading-tight mb-8"
            style={{ fontFamily: "'Outfit', sans-serif" }}
          >
            {t("problem.title_1")}
            <br />
            <span className="text-zinc-600 line-through">{t("problem.title_strike")}</span>{" "}
            <span className="text-[#FF7A00]">{t("problem.title_orange")}</span>.
          </h2>
          <div className="grid md:grid-cols-3 gap-6 mt-12">
            {[
              { icon: Clock, title: t("problem.card_1_title"), desc: t("problem.card_1_desc") },
              { icon: MessageCircle, title: t("problem.card_2_title"), desc: t("problem.card_2_desc") },
              { icon: BarChart3, title: t("problem.card_3_title"), desc: t("problem.card_3_desc") },
            ].map((p, i) => (
              <div
                key={i}
                className="p-6 rounded-2xl bg-zinc-950 border border-zinc-900 hover:border-[#FF7A00]/30 transition group"
                data-testid={`problem-${i}`}
              >
                <p.icon className="text-[#FF7A00] mb-4" size={28} />
                <h3 className="font-bold text-xl mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
                  {p.title}
                </h3>
                <p className="text-sm text-zinc-400 leading-relaxed">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURES BENTO */}
      <section id="features" className="relative py-24 md:py-32 px-6 md:px-12 z-10">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-xs uppercase tracking-[0.25em] text-[#FF7A00] font-bold mb-4">
              {t("features.kicker")}
            </p>
            <h2
              className="text-4xl md:text-6xl font-extrabold tracking-tight"
              style={{ fontFamily: "'Outfit', sans-serif" }}
            >
              {t("features.title")}
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
            {/* Big card: WhatsApp + IA */}
            <div className="md:col-span-8 p-8 rounded-2xl bg-gradient-to-br from-[#FF7A00]/15 via-zinc-950 to-zinc-950 border border-[#FF7A00]/30 relative overflow-hidden group hover:border-[#FF7A00]/50 transition" data-testid="feature-whatsapp">
              <div className="absolute -top-20 -right-20 w-64 h-64 bg-[#FF7A00]/20 rounded-full blur-3xl group-hover:scale-110 transition-transform duration-700" />
              <Bot className="text-[#FF7A00] mb-6 relative z-10" size={48} />
              <h3 className="text-3xl md:text-4xl font-extrabold mb-3 relative z-10" style={{ fontFamily: "'Outfit', sans-serif" }}>
                {t("features.whatsapp_title")}
              </h3>
              <p className="text-zinc-400 max-w-xl relative z-10">
                {t("features.whatsapp_desc")}
              </p>
            </div>
            {/* Small: Agenda */}
            <div className="md:col-span-4 p-8 rounded-2xl bg-zinc-950 border border-zinc-900 hover:border-zinc-700 transition" data-testid="feature-agenda">
              <Calendar className="text-[#FF7A00] mb-4" size={32} />
              <h3 className="text-2xl font-bold mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>{t("features.agenda_title")}</h3>
              <p className="text-sm text-zinc-400">{t("features.agenda_desc")}</p>
            </div>
            {/* Small: Facturación */}
            <div className="md:col-span-4 p-8 rounded-2xl bg-zinc-950 border border-zinc-900 hover:border-zinc-700 transition" data-testid="feature-billing">
              <Receipt className="text-[#FF7A00] mb-4" size={32} />
              <h3 className="text-2xl font-bold mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>{t("features.billing_title")}</h3>
              <p className="text-sm text-zinc-400">{t("features.billing_desc")}</p>
            </div>
            {/* Small: Pagos */}
            <div className="md:col-span-4 p-8 rounded-2xl bg-zinc-950 border border-zinc-900 hover:border-zinc-700 transition" data-testid="feature-payments">
              <CreditCard className="text-[#FF7A00] mb-4" size={32} />
              <h3 className="text-2xl font-bold mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>{t("features.payments_title")}</h3>
              <p className="text-sm text-zinc-400">{t("features.payments_desc")}</p>
            </div>
            {/* Small: Inventario */}
            <div className="md:col-span-4 p-8 rounded-2xl bg-zinc-950 border border-zinc-900 hover:border-zinc-700 transition" data-testid="feature-inventory">
              <Package className="text-[#FF7A00] mb-4" size={32} />
              <h3 className="text-2xl font-bold mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>{t("features.inventory_title")}</h3>
              <p className="text-sm text-zinc-400">{t("features.inventory_desc")}</p>
            </div>
            {/* Small: CRM */}
            <div className="md:col-span-4 p-8 rounded-2xl bg-zinc-950 border border-zinc-900 hover:border-zinc-700 transition" data-testid="feature-crm">
              <Users className="text-[#FF7A00] mb-4" size={32} />
              <h3 className="text-2xl font-bold mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>{t("features.crm_title")}</h3>
              <p className="text-sm text-zinc-400">{t("features.crm_desc")}</p>
            </div>
            {/* Wide: Dashboard */}
            <div className="md:col-span-12 p-8 rounded-2xl bg-zinc-950 border border-zinc-900 hover:border-zinc-700 transition flex flex-col md:flex-row items-center gap-6" data-testid="feature-dashboard">
              <BarChart3 className="text-[#FF7A00] flex-shrink-0" size={48} />
              <div className="flex-1">
                <h3 className="text-2xl md:text-3xl font-bold mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
                  {t("features.dashboard_title")}
                </h3>
                <p className="text-zinc-400">
                  {t("features.dashboard_desc")}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how" className="relative py-24 md:py-32 px-6 md:px-12 z-10">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-xs uppercase tracking-[0.25em] text-[#FF7A00] font-bold mb-4">{t("how.kicker")}</p>
            <h2 className="text-4xl md:text-6xl font-extrabold tracking-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>
              {t("how.title")}
            </h2>
          </div>
          <div className="space-y-6">
            {[
              { num: "01", title: t("how.step_1_title"), desc: t("how.step_1_desc") },
              { num: "02", title: t("how.step_2_title"), desc: t("how.step_2_desc") },
              { num: "03", title: t("how.step_3_title"), desc: t("how.step_3_desc") },
              { num: "04", title: t("how.step_4_title"), desc: t("how.step_4_desc") },
            ].map((step, i) => (
              <div key={i} className="flex gap-6 items-start group" data-testid={`step-${i}`}>
                <div className="text-5xl md:text-6xl font-black text-zinc-900 group-hover:text-[#FF7A00]/30 transition-colors w-24 flex-shrink-0" style={{ fontFamily: "'Outfit', sans-serif" }}>
                  {step.num}
                </div>
                <div className="flex-1 pt-2 pb-6 border-b border-zinc-900">
                  <h3 className="text-2xl md:text-3xl font-bold mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>{step.title}</h3>
                  <p className="text-zinc-400">{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" className="relative py-24 md:py-32 px-6 md:px-12 z-10">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-xs uppercase tracking-[0.25em] text-[#FF7A00] font-bold mb-4">
              {t("pricing.kicker")}
            </p>
            <h2 className="text-4xl md:text-6xl font-extrabold tracking-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>
              {t("pricing.title")}
            </h2>
            <p className="text-zinc-400 mt-4 text-lg">
              {t("pricing.subtitle")}
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {[
              {
                name: t("pricing.starter_name"),
                price: "29",
                tagline: t("pricing.starter_tagline"),
                features: t("pricing.starter_features", { returnObjects: true }),
                highlight: false,
                custom: false,
                test: "plan-starter",
                slug: "starter",
              },
              {
                name: t("pricing.business_name"),
                price: "59",
                tagline: t("pricing.business_tagline"),
                features: t("pricing.business_features", { returnObjects: true }),
                highlight: true,
                custom: false,
                test: "plan-business",
                slug: "business",
              },
              {
                name: t("pricing.custom_name"),
                price: t("pricing.custom_price_hint"),
                priceLabel: t("pricing.custom_price_label"),
                tagline: t("pricing.custom_tagline"),
                features: t("pricing.custom_features", { returnObjects: true }),
                highlight: false,
                custom: true,
                test: "plan-custom",
                slug: "custom",
              },
            ].map((plan) => (
              <div
                key={plan.slug}
                className={`relative p-8 rounded-2xl transition ${
                  plan.highlight
                    ? "bg-gradient-to-b from-[#FF7A00]/10 to-zinc-950 border-2 border-[#FF7A00] shadow-[0_0_60px_rgba(255,122,0,0.25)] md:-translate-y-4"
                    : plan.custom
                      ? "bg-gradient-to-b from-zinc-900/60 to-zinc-950 border border-zinc-700 hover:border-[#FF7A00]/40"
                      : "bg-zinc-950 border border-zinc-900 hover:border-zinc-700"
                }`}
                data-testid={plan.test}
              >
                {plan.highlight && (
                  <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#FF7A00] text-black font-bold border-0 px-3 py-1 uppercase tracking-[0.15em] text-[10px]">
                    {t("pricing.popular")}
                  </Badge>
                )}
                <p className="text-xs uppercase tracking-[0.2em] text-zinc-500 font-bold mb-2">{plan.tagline}</p>
                <h3 className="text-3xl font-extrabold mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
                  {plan.name}
                </h3>
                <div className="mb-6">
                  {plan.custom ? (
                    <div>
                      <p className="text-[11px] uppercase tracking-[0.2em] text-[#FF7A00]/80 font-bold mb-1">{plan.priceLabel}</p>
                      <span className="text-3xl md:text-4xl font-extrabold text-[#FF7A00]" style={{ fontFamily: "'Outfit', sans-serif" }}>
                        {plan.price}
                      </span>
                      <p className="text-[11px] text-zinc-500 mt-2 leading-relaxed">{t("pricing.custom_note")}</p>
                    </div>
                  ) : (
                    <>
                      <span className="text-5xl md:text-6xl font-extrabold" style={{ fontFamily: "'Outfit', sans-serif" }}>
                        ${plan.price}
                      </span>
                      <span className="text-zinc-500 ml-1">{t("pricing.month")}</span>
                    </>
                  )}
                </div>
                <ul className="space-y-3 mb-8 min-h-[280px]">
                  {(plan.features || []).map((f, i) => (
                    <li key={i} className="flex gap-2 items-start text-sm">
                      <Check size={16} className="text-[#FF7A00] flex-shrink-0 mt-0.5" />
                      <span className="text-zinc-300">{f}</span>
                    </li>
                  ))}
                </ul>
                <Button
                  onClick={() => {
                    if (plan.custom) {
                      window.dispatchEvent(new CustomEvent("open-sofia-chat"));
                    } else {
                      navigate(`/signup?plan=${plan.slug}`);
                    }
                  }}
                  className={`w-full h-12 font-bold ${
                    plan.highlight
                      ? "bg-[#FF7A00] hover:bg-[#FF8E1F] text-black"
                      : plan.custom
                        ? "bg-zinc-900 text-white hover:bg-zinc-800 border border-[#FF7A00]/40"
                        : "bg-white text-black hover:bg-zinc-200"
                  }`}
                  data-testid={`${plan.test}-cta`}
                >
                  {plan.custom ? t("pricing.cta_custom") : t("pricing.cta")}
                </Button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* BUSINESS TYPES */}
      <section className="relative py-24 px-6 md:px-12 border-y border-zinc-900 bg-zinc-950/30 z-10">
        <div className="max-w-6xl mx-auto text-center">
          <p className="text-xs uppercase tracking-[0.25em] text-[#FF7A00] font-bold mb-4">
            {t("types.kicker")}
          </p>
          <h2 className="text-3xl md:text-5xl font-extrabold tracking-tight mb-12" style={{ fontFamily: "'Outfit', sans-serif" }}>
            {t("types.title")}
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 max-w-4xl mx-auto text-sm">
            {[
              "💉 Estética avanzada", "💅 Salón de uñas", "👁️ Pestañas / cejas", "🧖‍♀️ Spa / Masajes",
              "💇‍♀️ Salón de belleza", "💄 Centro estético facial", "✂️ Peluquería / Barbería", "🍔 Restaurante",
              "🛒 Colmado", "📱 Tienda de teléfonos", "👕 Tienda de ropa", "📞 Call Center",
              "🔧 Taller / Mecánico", "🚚 Mensajería", "📦 Delivery WhatsApp", "⚡ Y muchos más",
            ].map((b, i) => (
              <div
                key={i}
                className="p-3 rounded-lg border border-zinc-900 hover:border-[#FF7A00]/50 hover:bg-[#FF7A00]/5 transition cursor-default"
                data-testid={`business-type-${i}`}
              >
                {b}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="relative py-24 md:py-32 px-6 md:px-12 z-10">
        <div className="max-w-4xl mx-auto text-center relative">
          <div className="absolute inset-0 bg-[#FF7A00]/10 blur-3xl -z-10" />
          <h2 className="text-4xl md:text-6xl font-extrabold tracking-tight mb-6" style={{ fontFamily: "'Outfit', sans-serif" }}>
            {t("final_cta.title_1")}
            <br />
            <span className="text-[#FF7A00]">{t("final_cta.title_2")}</span>.
          </h2>
          <p className="text-zinc-400 text-lg mb-10 max-w-xl mx-auto">
            {t("final_cta.subtitle")}
          </p>
          <Button
            onClick={() => navigate("/signup")}
            size="lg"
            className="bg-[#FF7A00] hover:bg-[#FF8E1F] text-black font-bold text-lg px-10 h-16 shadow-[0_0_60px_rgba(255,122,0,0.5)] hover:scale-105 transition-all"
            data-testid="final-cta"
          >
            {t("final_cta.cta")} <ChevronRight className="ml-1" size={22} />
          </Button>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="relative border-t border-zinc-900 bg-black/80 backdrop-blur-sm z-10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-12 grid md:grid-cols-4 gap-8 text-sm">
          <div className="md:col-span-2">
            <div className="flex items-center mb-4">
              <img src="/brand/commandix-logo-tight.png" alt="Commandix" className="h-12 object-contain" draggable="false" />
            </div>
            <p className="text-zinc-500 max-w-sm leading-relaxed">
              {t("footer.tagline")}
            </p>
          </div>
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-zinc-500 font-bold mb-4">{t("footer.product")}</p>
            <ul className="space-y-2 text-zinc-400">
              <li><a href="#features" className="hover:text-white">{t("nav.features")}</a></li>
              <li><a href="#pricing" className="hover:text-white">{t("nav.pricing")}</a></li>
              <li><a href="#how" className="hover:text-white">{t("nav.how")}</a></li>
              <li><Link to="/login" className="hover:text-white">{t("nav.login")}</Link></li>
            </ul>
          </div>
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-zinc-500 font-bold mb-4">{t("footer.payment")}</p>
            <div className="flex items-center gap-3 text-zinc-500">
              <FaPaypal size={28} title="PayPal" data-testid="footer-paypal" />
              <FaBitcoin size={28} title="Bitcoin" className="text-[#F7931A]" data-testid="footer-btc" />
              <SiTether size={28} title="USDT" className="text-[#26A17B]" data-testid="footer-usdt" />
            </div>
            <p className="text-xs text-zinc-600 mt-3">{t("footer.secure")}</p>
          </div>
        </div>
        <div className="border-t border-zinc-900 px-6 md:px-12 py-6 text-xs text-zinc-600 flex flex-col md:flex-row items-center justify-between gap-2">
          <p>© {new Date().getFullYear()} Commandix CRM · {t("footer.rights")}</p>
          <p className="flex items-center gap-1.5">
            <Globe size={12} /> commandixcrm.com
            <span className="mx-1">·</span>
            <Shield size={12} /> {t("footer.encryption")}
          </p>
        </div>
      </footer>
      <SofiaChatWidget />
    </div>
  );
}
